import { forwardRef, useCallback, useId } from "react";
import { clsx } from "clsx";

import { Text } from "../text/Text";
import { Icon } from "../icon/Icon";

import "./Checkbox.css";

import type { ReactNode, ChangeEvent } from "react";
import type { IntentProp } from "../components";

export interface CheckboxChangeEvent {
  checked: boolean;
}

export interface CheckboxProps {
  appearance?: "default" | "card";
  checked?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  indeterminate?: boolean;
  intent?: Extract<IntentProp, "success" | "error">;
  name?: string;
  readOnly?: boolean;
  value?: string;
  onChange?: (event: CheckboxChangeEvent) => void;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function CheckboxImpl(
    {
      appearance,
      checked,
      children,
      disabled,
      indeterminate,
      intent,
      name,
      readOnly,
      value,
      onChange,
    },
    ref,
  ) {
    const id = useId();

    const renderIcon = useCallback(() => {
      if (intent === "success") {
        return <Icon icon="square-check" variant="solid" size="lg" />;
      }

      if (intent === "error") {
        return <Icon icon="square-xmark" variant="solid" size="lg" />;
      }

      if (indeterminate) {
        return <Icon icon="square-minus" variant="solid" size="lg" />;
      }

      if (checked) {
        return <Icon icon="square-check" variant="solid" size="lg" />;
      }

      if (disabled) {
        return <Icon icon="square" variant="solid" size="lg" />;
      }

      return <Icon icon="square" size="lg" />;
    }, [intent, indeterminate, checked, disabled]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.({ checked: e.target.checked });
      },
      [onChange],
    );

    return (
      <label
        className={clsx("Checkbox", {
          "Checkbox--disabled": disabled,
          "Checkbox--readonly": readOnly,
          [`Checkbox--${appearance}`]: appearance,
          [`Checkbox--${intent}`]: intent,
        })}
        htmlFor={id}
      >
        <input
          ref={ref}
          type={"checkbox"}
          id={id}
          checked={Boolean(checked || indeterminate)}
          name={name}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          onChange={handleChange}
        />

        <div className="Checkbox__indicator">{renderIcon()}</div>

        <Text>{children}</Text>
      </label>
    );
  },
);
